import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { useGetLoanAgreementHistory } from '../../../services/loanService';
import { format } from 'date-fns';

interface AgreementItem {
    id: string;
    applicationId: string;
    applicationNumber: string;
    customerName: string;
    customerBusinessName?: string;
    documentId?: string;
    processedBy: string;
    processedDate: string;
    createdDate: string;
}

const LoanAgreement = () => {
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(20);
    const ALL_AGREEMENTS_PAGE_SIZE = 10000;

    // Fetch loan agreement history
    const {
        data: loanAgreementHistory,
        isLoading,
        error
    } = useGetLoanAgreementHistory({
        page: 0,
        size: ALL_AGREEMENTS_PAGE_SIZE
    });

    const columns = [
        {
            name: 'Application #',
            selector: (row: AgreementItem) => row.applicationNumber,
            sortable: true,
            cell: (row: AgreementItem) => (
                <Link to={`/loan-agreement/${row.applicationId}`} className="text-primary">
                    {row.applicationNumber}
                </Link>
            ),
        },
        {
            name: 'Customer',
            selector: (row: AgreementItem) => row.customerBusinessName || row.customerName,
            sortable: true,
            cell: (row: AgreementItem) => (
                <div>
                    {row.customerBusinessName ? (
                        <>
                            <div>{row.customerBusinessName}</div>
                            <small className="text-muted">{row.customerName}</small>
                        </>
                    ) : (
                        row.customerName
                    )}
                </div>
            ),
        },
        {
            name: 'Processed By',
            selector: (row: AgreementItem) => row.processedBy,
            sortable: true,
        },
        {
            name: 'Processed Date',
            selector: (row: AgreementItem) => row.processedDate,
            cell: (row: AgreementItem) => format(new Date(row.processedDate), 'dd MMM yyyy HH:mm'),
            sortable: true,
        },
        {
            name: 'Created Date',
            selector: (row: AgreementItem) => row.createdDate,
            cell: (row: AgreementItem) => format(new Date(row.createdDate), 'dd MMM yyyy HH:mm'),
            sortable: true,
        }
    ];

    // Calculate statistics from the history data
    const stats = [
        {
            title: 'Total Agreements',
            value: (loanAgreementHistory?.content?.length || 0).toString(),
            icon: 'fa-file-contract',
            color: 'primary'
        },
        {
            title: 'Processed Today',
            value: loanAgreementHistory?.content?.filter(item => {
                if (!item.processedDate) return false;
                const today = new Date();
                const processedDate = new Date(item.processedDate);
                return processedDate.toDateString() === today.toDateString();
            }).length.toString() || '0',
            icon: 'fa-clock',
            color: 'warning'
        },
        {
            title: 'This Week',
            value: loanAgreementHistory?.content?.filter(item => {
                if (!item.processedDate) return false;
                const today = new Date();
                const processedDate = new Date(item.processedDate);
                const weekAgo = new Date(today.setDate(today.getDate() - 7));
                return processedDate >= weekAgo;
            }).length.toString() || '0',
            icon: 'fa-calendar-week',
            color: 'info'
        },
        {
            title: 'This Month',
            value: loanAgreementHistory?.content?.filter(item => {
                if (!item.processedDate) return false;
                const today = new Date();
                const processedDate = new Date(item.processedDate);
                const monthAgo = new Date(today.setMonth(today.getMonth() - 1));
                return processedDate >= monthAgo;
            }).length.toString() || '0',
            icon: 'fa-calendar-alt',
            color: 'success'
        }
    ];

    const handlePageChange = (page: number) => {
        setPage(page);
    };

    const handlePerRowsChange = (newSize: number, page: number) => {
        setSize(newSize);
        setPage(page);
    };

    return (
        <div>
            <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
                <div className="container-fluid">
                    <div className="row g-3 mb-3 align-items-center">
                        <div className="col">
                            <ol className="breadcrumb bg-transparent mb-0">
                                <li className="breadcrumb-item"><Link className="text-secondary" to="/">Home</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Loan Agreement</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
                <div className="container-fluid">
                    {/* Statistics Cards */}
                    <div className="row g-3 mb-3 row-deck">
                        {stats.map((stat, index) => (
                            <div key={index} className="col-lg-3 col-md-6 col-sm-6">
                                <div className="card">
                                    <div className="card-body">
                                        <div className={`text-${stat.color}`}>
                                            <i className={`fa ${stat.icon} fa-lg`}></i>
                                            <span className="ms-2">{stat.title}</span>
                                        </div>
                                        <h3 className="mt-2 mb-0">{stat.value}</h3>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Loan Agreement History Table */}
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header">
                                    <h6 className="card-title mb-0">Loan Agreement History</h6>
                                </div>
                                <div className="card-body">
                                    {error ? (
                                        <div className="alert alert-danger">
                                            Failed to load loan agreement history: {(error as Error).message}
                                        </div>
                                    ) : (
                                        <DataTable
                                            columns={columns}
                                            data={(loanAgreementHistory?.content || []) as AgreementItem[]}
                                            progressPending={isLoading}
                                            pagination
                                            onChangeRowsPerPage={handlePerRowsChange}
                                            onChangePage={handlePageChange}
                                            paginationPerPage={size}
                                            paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                            dense
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoanAgreement; 
